import * as React from 'react'

import { TentacleError } from 'modules/core/lib/errors'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { type ReportEditorStore } from 'modules/reports/lib/Store'
import Report from 'modules/reports/store/models/Report'
import type { ResourceData } from 'modules/reports/store/models/Resource'

const DEFAULT_SERVER_ERROR = 'SERVER_ERROR'
const DEFAULT_SERVER_STATUS_CODE = 500

type Result = {
  fetchData: () => Promise<void>
  loading: boolean
} & (
  | {
      results: ResourceData | undefined
      error: undefined
    }
  | {
      results: undefined
      error: TentacleError
    }
)

export const useExecuteReportQuery = (store: ReportEditorStore): Result => {
  const [loading, setLoading] = React.useState(false)

  const query = store.sqlQuery
  const variables = store.variables

  async function fetchData() {
    if (!query || loading) return

    setLoading(true)

    try {
      const startRequestTime = new Date().getTime()
      const response = await Report.executeQuery(query, variables, undefined)

      const finishRequestTime = new Date().getTime()

      if (response.status > 299) {
        const tentacleError = new TentacleError({
          message: response.data.message,
          code: response.data.code,
          error: DEFAULT_SERVER_ERROR,
          status_code: response.status,
        })
        store.setSqlQueryError(query, tentacleError)

        return
      }

      const lastUpdatedHeader = response.headers['x-reports-last-updated']
      store.setSqlQueryResult(query, {
        lastUpdated: lastUpdatedHeader && new Date(Date.parse(lastUpdatedHeader)),
        engine: response.headers['x-reports-engine'],
        rows: response.data,
        status: 'ok',
      })

      app.setFlash({
        type: 'correct',
        message: i18n.t('reports.sql_editor.success', {
          rows: response.data.length,
          time: finishRequestTime - startRequestTime,
        }),
      })
    } catch (error: unknown) {
      const exception = error as Error

      const tentacleError = new TentacleError({
        message: exception.message,
        code: exception.name,
        error: DEFAULT_SERVER_ERROR,
        status_code: DEFAULT_SERVER_STATUS_CODE,
      })
      store.setSqlQueryError(query, tentacleError)
    } finally {
      setLoading(false)
    }
  }

  if (store.sqlQueryError && !loading) {
    return { fetchData, results: undefined, error: store.sqlQueryError, loading }
  }

  return { fetchData, results: store.sqlQueryResult, error: undefined, loading }
}
